import React from "react";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Calendar, ArrowRight, Facebook, Twitter, Linkedin, Instagram, Youtube } from "lucide-react";

const Press = () => {
  const featuredNews = {
    title: "Quantum Workplace Acquires Assembly, Strengthening Its Talent Management Platform With Addition of Rewards",
    excerpt: "Quantum Workplace acquires Assembly to enrich its talent management platform—connecting recognition and rewards data to engagement, performance, and retention signals.",
    date: "January 26, 2026",
    source: "PRWeb",
    logo: "/lovable-uploads/prweb-logo.png",
    url: "https://www.prweb.com/releases/quantum-workplace-acquires-assembly-strengthening-its-talent-management-platform-with-addition-of-rewards-302670139.html"
  };

  const pressItems = [
    {
      source: "PRWeb",
      title: "Quantum Workplace Acquires Assembly, Strengthening Its Talent Management Platform With Addition of Rewards",
      excerpt: "Quantum Workplace acquires Assembly to enrich its talent management platform—connecting recognition and rewards data to engagement, performance, and retention signals.",
      date: "January 26, 2026",
      category: "Featured news",
      url: "https://www.prweb.com/releases/quantum-workplace-acquires-assembly-strengthening-its-talent-management-platform-with-addition-of-rewards-302670139.html"
    },
    {
      source: "PRWeb",
      title: "Assembly Unveils AI-Powered Employee Recognition Platform Transforming Workplace Appreciation",
      excerpt: "Assembly launches AI tools that cut recognition time from minutes to seconds, driving 10% higher engagement and saving 50,000+ hours for managers.",
      date: "July 24, 2025",
      category: "Featured news",
      url: "https://www.prweb.com/releases/assembly-unveils-ai-powered-employee-recognition-platform-transforming-workplace-appreciation-with-lightning-fast-recognition-and-unmatched-support-302511411.html"
    },
    {
      source: "The Software Report",
      title: "Revolutionizing Company Collaboration: Assembly's Jonathan Fields",
      excerpt: "Jonathan Fields shares insights on how Assembly is transforming workplace collaboration and employee recognition through innovative technology and human-centered design.",
      date: "March 25, 2025",
      category: "Interview",
      url: "https://www.thesoftwarereport.com/revolutionizing-company-collaboration-assemblys-jonathan-fields/"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Press | Assembly - Employee Recognition Platform</title>
        <meta name="description" content="See what the industry has to say about Assembly's award-winning employee recognition and rewards platform." />
        <meta name="keywords" content="Assembly press, employee recognition news, workplace engagement awards, HR technology press" />
        <meta property="og:title" content="Press | Assembly" />
        <meta property="og:description" content="See what the industry has to say about Assembly's award-winning employee recognition and rewards platform." />
        <meta property="og:site_name" content="Assembly" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://lovable.dev/opengraph-image-p98pqg.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Press | Assembly" />
        <meta name="twitter:description" content="See what the industry has to say about Assembly's award-winning employee recognition and rewards platform." />
        <meta name="twitter:image" content="https://lovable.dev/opengraph-image-p98pqg.png" />
        {typeof window !== 'undefined' && <link rel="canonical" href={window.location.href} />}
      </Helmet>
      
      <div className="flex flex-col min-h-screen w-full bg-[#F7F5FF]">
        <Navbar />
        
        <main className="flex-grow w-full">
          {/* Header Section */}
          <section className="pt-24 pb-4 lg:pt-32 lg:pb-6">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <div className="max-w-4xl">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                      Press
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed">
                      See what the industry has to say about Assembly's award-winning 
                      employee recognition and rewards platform.
                    </p>
                  </div>
                  <div className="flex gap-3 ml-8 mt-2">
                    <a href="https://www.facebook.com/gotojoinassembly" target="_blank" rel="noopener noreferrer" aria-label="Assembly's Facebook page" className="text-gray-600 hover:text-assembly-orange transition-colors">
                      <Facebook className="h-4 w-4" />
                    </a>
                    <a href="https://twitter.com/join_assembly" target="_blank" rel="noopener noreferrer" aria-label="Assembly's Twitter page" className="text-gray-600 hover:text-assembly-orange transition-colors">
                      <Twitter className="h-4 w-4" />
                    </a>
                    <a href="https://www.linkedin.com/company/joinassembly/" target="_blank" rel="noopener noreferrer" aria-label="Assembly's LinkedIn page" className="text-gray-600 hover:text-assembly-orange transition-colors">
                      <Linkedin className="h-4 w-4" />
                    </a>
                    <a href="https://www.instagram.com/join_assembly/" target="_blank" rel="noopener noreferrer" aria-label="Assembly's Instagram page" className="text-gray-600 hover:text-assembly-orange transition-colors">
                      <Instagram className="h-4 w-4" />
                    </a>
                    <a href="https://www.youtube.com/channel/UC8iP6SSNi5eAE_Nd5EaShCQ" target="_blank" rel="noopener noreferrer" aria-label="Assembly's YouTube channel" className="text-gray-600 hover:text-assembly-orange transition-colors">
                      <Youtube className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Featured News Section */}
          <section className="py-6">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <Badge variant="secondary" className="mb-6">
                Featured news
              </Badge>
              
              <Card className="border-2 border-primary/20 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 p-2">
                      <img 
                        src="/logos/prweb-logo.jpeg" 
                        alt="PRWeb"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight cursor-pointer hover:text-primary transition-colors"
                          onClick={() => window.open(featuredNews.url, '_blank')}>
                        {featuredNews.title}
                      </h2>
                      <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                        {featuredNews.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="font-medium">{featuredNews.source}</span>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {featuredNews.date}
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          className="gap-2"
                          onClick={() => window.open(featuredNews.url, '_blank')}
                        >
                          Read more
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* All Assembly Press Section */}
          <section className="py-8">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-12">
                All Assembly press
              </h2>
              
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {pressItems.map((item, index) => (
                  <Card key={index} className="bg-white hover:shadow-xl transition-all duration-300 group cursor-pointer overflow-hidden"
                        onClick={() => window.open(item.url, '_blank')}>
                    <CardContent className="p-0">
                      <div className="aspect-video bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-8">
                        <img 
                          src={item.source === "The Software Report" ? "/logos/software-report-logo.svg" : "/logos/prweb-logo.jpeg"}
                          alt={item.source}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <Badge variant="outline" className="text-xs">
                            {item.category}
                          </Badge>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {item.date}
                          </div>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-tight group-hover:text-primary transition-colors line-clamp-2">
                          {item.title}
                        </h3>
                        
                        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
                          {item.excerpt}
                        </p>
                        
                        <Button variant="ghost" size="sm" className="gap-1 text-xs w-full justify-center">
                          Read more
                          <ArrowRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default Press;